// ============================================================
// GOOGLE WORKSPACE API (C2) — ログインユーザーの実 Googleカレンダー読み取り。
//
// Firebase Auth の Google ログイン（signInWithPopup）で得た OAuth アクセストークン
// （AuthContext の googleAccessToken）を Bearer として、Calendar API v3 を直叩きする。
// SDK を足さず fetch のみ（バンドルを軽く保つ）。返り値はアプリ内部の
// SynthesizedCalendarEntry 形に正規化し、Calendar.tsx で既存の合成予定とマージする。
//
// ⚠️ トークンは短命（〜1時間・リフレッシュ不可・AuthContext 参照）。失効後の 401 は
// エラーにせず空配列で握りつぶし、UI 側は「未接続」相当の表示に倒す（落とさない）。
// TODO(C堅牢化): 401 を検知して「Google連携を更新」導線へ誘導する。
// ============================================================
import type { SynthesizedCalendarEntry } from '../types/models';

const CALENDAR_EVENTS_ENDPOINT =
  'https://www.googleapis.com/calendar/v3/calendars/primary/events';

/** Calendar API のイベント1件（使うフィールドだけを最小宣言）。 */
interface GoogleCalendarApiEvent {
  id?: string;
  status?: string; // 'confirmed' | 'tentative' | 'cancelled'
  summary?: string;
  hangoutLink?: string;
  htmlLink?: string;
  start?: { date?: string; dateTime?: string; timeZone?: string };
  end?: { date?: string; dateTime?: string; timeZone?: string };
}

interface GoogleCalendarApiResponse {
  items?: GoogleCalendarApiEvent[];
  /** 次ページのトークン（無ければ最終ページ）。 */
  nextPageToken?: string;
}

export interface CalendarRange {
  /** RFC3339（例 '2026-06-01T00:00:00Z'）。 */
  timeMin: string;
  /** RFC3339。 */
  timeMax: string;
}

/** ローカルタイムの 'YYYY-MM-DD'（UTC ズレを避けるため getFullYear 系で組む）。 */
function toLocalYmd(d: Date): string {
  const p = (x: number) => String(x).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

/** ローカルタイムの 'HH:mm'。 */
function toLocalHm(d: Date): string {
  const p = (x: number) => String(x).padStart(2, '0');
  return p(d.getHours()) + ':' + p(d.getMinutes());
}

/**
 * 「今月前後」の取得レンジを作る（anchor 月の前月1日〜翌月末）。
 * 既定 anchor は今日。Calendar.tsx の月送りに依存せず、十分広い窓を一度に取る。
 */
export function defaultCalendarRange(anchor: Date = new Date()): CalendarRange {
  const y = anchor.getFullYear();
  const m = anchor.getMonth(); // 0-based
  // 前月の1日 00:00（ローカル）〜 翌月末日の終わり（翌々月0日=翌月末）。
  const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const end = new Date(y, m + 2, 0, 23, 59, 59, 999);
  return { timeMin: start.toISOString(), timeMax: end.toISOString() };
}

/**
 * 1件の API イベント → 内部 SynthesizedCalendarEntry。
 * 終日予定（start.date）は time なし、時刻あり（start.dateTime）は 'HH:mm'。
 * cancelled / 日付なしは null（呼び出し側で除外）。
 */
function mapEvent(ev: GoogleCalendarApiEvent): SynthesizedCalendarEntry | null {
  if (ev.status === 'cancelled') return null;
  const startDate = ev.start?.date; // 終日
  const startDateTime = ev.start?.dateTime; // 時刻あり
  let date: string;
  let time: string | undefined;
  if (startDateTime) {
    const d = new Date(startDateTime);
    if (isNaN(d.getTime())) return null;
    date = toLocalYmd(d);
    time = toLocalHm(d);
  } else if (startDate) {
    date = startDate; // すでに 'YYYY-MM-DD'
  } else {
    return null;
  }
  const meetLink = ev.hangoutLink || undefined;
  return {
    id: 'gcal-' + (ev.id || date + '-' + (ev.summary || '')),
    // Google 予定は会議リンク有無に関わらず「予定（plan）」として扱う。
    // 種別の取り違えを避けつつ、meetLink があれば参加導線は出せる。
    kind: 'plan',
    date,
    time,
    title: ev.summary || '(タイトルなし)',
    meetLink,
    source: 'google',
  };
}

/**
 * ログインユーザーの primary カレンダーから、指定レンジの予定を取得して正規化する。
 *
 * @param accessToken Google OAuth アクセストークン（AuthContext.googleAccessToken）。
 *                    null/空なら即 []（未連携・dev）。
 * @param range       取得レンジ（省略時 defaultCalendarRange()＝今月前後）。
 * @returns           SynthesizedCalendarEntry[]（source='google'）。失敗時は []。
 *
 * エラー（ネットワーク / 401 失効 / 非200）は throw せず空配列＋console.warn。
 * UI を落とさず「未接続相当」に倒す方針。
 */
export async function fetchGoogleCalendarEvents(
  accessToken: string | null | undefined,
  range?: CalendarRange,
): Promise<SynthesizedCalendarEntry[]> {
  if (!accessToken) return [];
  const r = range || defaultCalendarRange();
  // ⚠️ maxResults は 1ページ最大 250。予定が多いカレンダー（繰り返し予定の展開含む）では
  //    timeMin から古い順に 250 件で打ち切られ、表示中の週（=レンジの後半）が取得されない。
  //    nextPageToken を辿って全件取得する（暴走防止に MAX_PAGES で上限）。
  const MAX_PAGES = 8; // 250 × 8 = 最大 2000 件
  const all: GoogleCalendarApiEvent[] = [];
  let pageToken: string | undefined;
  try {
    for (let page = 0; page < MAX_PAGES; page++) {
      const params = new URLSearchParams({
        timeMin: r.timeMin,
        timeMax: r.timeMax,
        singleEvents: 'true', // 繰り返し予定を実体化
        orderBy: 'startTime',
        maxResults: '250',
      });
      if (pageToken) params.set('pageToken', pageToken);
      const res = await fetch(CALENDAR_EVENTS_ENDPOINT + '?' + params.toString(), {
        headers: { Authorization: 'Bearer ' + accessToken },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        // 401 = トークン失効（短命のため通常運用でも起こる）。落とさず、取得済み分だけ返す。
        console.warn(
          '[google] Calendar API 取得失敗 status=' + res.status + ' body=' + body.slice(0, 300),
        );
        break;
      }
      const data: GoogleCalendarApiResponse = await res.json();
      if (data.items) all.push(...data.items);
      pageToken = data.nextPageToken;
      if (!pageToken) break; // 最終ページ
    }
    return all
      .map(mapEvent)
      .filter((e): e is SynthesizedCalendarEntry => e !== null);
  } catch (err) {
    console.warn('[google] Calendar API 例外', err);
    return all.map(mapEvent).filter((e): e is SynthesizedCalendarEntry => e !== null);
  }
}

// ============================================================
// MEET議事録の取り込み（2026-07-10） — 過去の会議一覧 ＋ Docs 本文取得。
//
// ① fetchRecentMeetings: Calendar API events で「過去 daysBack 日の会議」を
//    タイトル・添付Doc（Meet/Gemini の議事録Doc）付きで一覧化する。**中身は取らない**
//    （オプトイン方式：メンバーが「議事録を取得する」を押した会議だけ ② を呼ぶ）。
// ② fetchGoogleDocText: Docs API（scope documents.readonly・AuthContext C1 で要求）で
//    議事録Doc の本文をプレーンテキスト化する。403/404 は呼び出し側が再連携導線等に
//    振り分けるため、既存関数群と違い **握りつぶさず GoogleApiError を throw** する。
// ============================================================

/** Google API 直叩きの HTTP エラー（status で 401/403=要再連携、404=Doc消失 を判別）。 */
export class GoogleApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'GoogleApiError';
    this.status = status;
  }
}

/** 会議イベントに添付された Google ドキュメント（mimeType が Google Docs のものだけ）。 */
export interface MeetingDocAttachment {
  fileId: string;
  title: string;
}

/** 過去会議一覧の1件（タイトル等のメタのみ・議事録本文は含まない）。 */
export interface RecentMeeting {
  /** Calendar の event id（meetingImports の doc id に使う）。 */
  eventId: string;
  title: string;
  /** 開始日 'YYYY-MM-DD'（ローカル）。 */
  date: string;
  /** 開始時刻 'HH:mm'（終日イベントは undefined）。 */
  time?: string;
  /** 参加者数（attendees の要素数・自分のみ等で 0 のこともある）。 */
  attendeeCount: number;
  /** 参加者のメールアドレス（Calendar attendees(email) 由来・空要素は除外）。
   *  Meet取り込みの participantIds 解決（members の email と突合）の一次ソースに使う。 */
  attendeeEmails: string[];
  /** 添付された議事録Doc 候補（Google Docs のみ）。空＝議事録が見つからない会議。 */
  docAttachments: MeetingDocAttachment[];
}

/** Calendar API イベント（会議一覧用・attendees / attachments まで読む最小宣言）。 */
interface GoogleCalendarMeetingApiEvent {
  id?: string;
  status?: string;
  summary?: string;
  hangoutLink?: string;
  start?: { date?: string; dateTime?: string };
  attendees?: { email?: string }[];
  attachments?: { fileId?: string; title?: string; mimeType?: string }[];
}

const GOOGLE_DOC_MIME = 'application/vnd.google-apps.document';

/**
 * 過去 daysBack 日（既定14日）の「会議」をカレンダーから取得する（メタのみ・本文なし）。
 *
 * - timeMin=daysBack日前〜timeMax=now・singleEvents・orderBy=startTime で取得。
 * - attachments を fields に含め、mimeType が Google Docs の添付だけ docAttachments に拾う
 *   （Meet の「メモを作成」/ Gemini 議事録は Docs としてイベントに添付される）。
 * - 「会議らしさ」フィルタ：参加者あり or Meetリンクあり or Doc添付あり のイベントのみ
 *   （終日の個人ブロック等を除く）。cancelled は除外。
 * - 返り値は開始日時の降順（新しい順）。
 * - エラー・未連携は throw せず []（既存 fetchGoogleCalendarEvents と同じ「落とさない」方針。
 *   一覧が出ないだけで UI は成立する）。
 */
export async function fetchRecentMeetings(
  accessToken: string | null | undefined,
  daysBack = 14,
): Promise<RecentMeeting[]> {
  if (!accessToken) return [];
  const now = new Date();
  const timeMin = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000).toISOString();
  const timeMax = now.toISOString();
  const all: GoogleCalendarMeetingApiEvent[] = [];
  let pageToken: string | undefined;
  try {
    // 14日分なら通常1ページで足りるが、繰り返し予定の展開に備えて最大4ページ（250×4）。
    for (let page = 0; page < 4; page++) {
      const params = new URLSearchParams({
        timeMin,
        timeMax,
        singleEvents: 'true',
        orderBy: 'startTime',
        maxResults: '250',
        // attachments は既定応答に含まれないことがあるため fields で明示する。
        fields:
          'items(id,status,summary,hangoutLink,start,attendees(email),attachments(fileId,title,mimeType)),nextPageToken',
      });
      if (pageToken) params.set('pageToken', pageToken);
      const res = await fetch(CALENDAR_EVENTS_ENDPOINT + '?' + params.toString(), {
        headers: { Authorization: 'Bearer ' + accessToken },
      });
      if (!res.ok) {
        console.warn('[google] 会議一覧の取得失敗 status=' + res.status);
        break;
      }
      const data = (await res.json()) as { items?: GoogleCalendarMeetingApiEvent[]; nextPageToken?: string };
      if (data.items) all.push(...data.items);
      pageToken = data.nextPageToken;
      if (!pageToken) break;
    }
  } catch (err) {
    console.warn('[google] 会議一覧の取得例外', err);
  }
  const meetings: RecentMeeting[] = [];
  for (const ev of all) {
    if (!ev.id || ev.status === 'cancelled') continue;
    // 日付・時刻（終日イベントは date のみ）。
    let date: string;
    let time: string | undefined;
    if (ev.start?.dateTime) {
      const d = new Date(ev.start.dateTime);
      if (isNaN(d.getTime())) continue;
      date = toLocalYmd(d);
      time = toLocalHm(d);
    } else if (ev.start?.date) {
      date = ev.start.date;
    } else {
      continue;
    }
    const docAttachments: MeetingDocAttachment[] = (ev.attachments || [])
      .filter((a) => a.mimeType === GOOGLE_DOC_MIME && !!a.fileId)
      .map((a) => ({ fileId: a.fileId as string, title: a.title || '議事録ドキュメント' }));
    const attendeeCount = ev.attendees?.length ?? 0;
    const attendeeEmails = (ev.attendees || [])
      .map((a) => (typeof a.email === 'string' ? a.email.trim() : ''))
      .filter((e) => e.length > 0);
    // 会議らしさ: 参加者 or Meetリンク or Doc添付 のいずれかを持つイベントのみ。
    if (attendeeCount === 0 && !ev.hangoutLink && docAttachments.length === 0) continue;
    meetings.push({
      eventId: ev.id,
      title: ev.summary || '(タイトルなし)',
      date,
      time,
      attendeeCount,
      attendeeEmails,
      docAttachments,
    });
  }
  // 新しい順（date + time の文字列比較で足りる）。
  return meetings.sort((a, b) => (b.date + (b.time || '')).localeCompare(a.date + (a.time || '')));
}

// ── Docs API（本文のプレーンテキスト化）──
const DOCS_API = 'https://docs.googleapis.com/v1/documents/';

/** Docs API body.content の構造要素（使う部分だけの最小宣言・再帰）。 */
interface DocsStructuralElement {
  paragraph?: { elements?: { textRun?: { content?: string } }[] };
  table?: { tableRows?: { tableCells?: { content?: DocsStructuralElement[] }[] }[] };
  tableOfContents?: { content?: DocsStructuralElement[] };
}

/** paragraph / table / tableOfContents を再帰的に辿ってテキストを out に集める。 */
function collectDocsText(elements: DocsStructuralElement[] | undefined, out: string[]): void {
  for (const el of elements || []) {
    if (el.paragraph) {
      // textRun.content は段落末尾の '\n' を含むため、そのまま連結すれば行が保たれる。
      const line = (el.paragraph.elements || []).map((e) => e.textRun?.content || '').join('');
      if (line) out.push(line);
    }
    if (el.table) {
      for (const row of el.table.tableRows || []) {
        for (const cell of row.tableCells || []) collectDocsText(cell.content, out);
      }
    }
    if (el.tableOfContents) collectDocsText(el.tableOfContents.content, out);
  }
}

/**
 * Google ドキュメント（議事録Doc）の本文をプレーンテキスト化して返す。
 * scope: documents.readonly（AuthContext の GOOGLE_API_SCOPES・2026-07-10 追加）。
 *
 * ⚠️ 既存の Calendar/Tasks 系と違い、失敗は **GoogleApiError を throw** する。
 *   401/403 = トークン失効 or documents.readonly 未同意（→ 呼び出し側が再連携導線へ）、
 *   404 = Doc が削除済み/権限なし。呼び出し側で status を見て文言を出し分ける。
 */
export async function fetchGoogleDocText(
  accessToken: string | null | undefined,
  fileId: string,
): Promise<string> {
  if (!accessToken) throw new GoogleApiError(401, 'Google 未連携です（アクセストークンがありません）');
  if (!fileId) throw new GoogleApiError(404, '議事録ドキュメントが指定されていません');
  let res: Response;
  try {
    const params = new URLSearchParams({ fields: 'title,body.content' });
    res = await fetch(DOCS_API + encodeURIComponent(fileId) + '?' + params.toString(), {
      headers: { Authorization: 'Bearer ' + accessToken },
    });
  } catch {
    // ネットワーク断等。0 を「HTTP 以前の失敗」として扱う（再連携ではなく再試行系の文言へ）。
    throw new GoogleApiError(0, 'Google ドキュメントへの接続に失敗しました');
  }
  if (!res.ok) {
    throw new GoogleApiError(res.status, 'Google ドキュメントの取得に失敗しました（status ' + res.status + '）');
  }
  const data = (await res.json()) as { body?: { content?: DocsStructuralElement[] } };
  const out: string[] = [];
  collectDocsText(data.body?.content, out);
  return out.join('').trim();
}

// ============================================================
// GOOGLE TASKS API (C3) — ログインユーザーの実 Google ToDo（タスク）読み書き。
//
// scope 'https://www.googleapis.com/auth/tasks'（C1 で要求済み）の Bearer で
// Tasks API v1 を直叩き。読み取り（全リスト横断）＋ 完了/未完了の書き戻し（PATCH）。
// アプリ自前タスク（議事録由来）とは別物として MyTasks の専用セクションに表示する。
// ============================================================
const TASKS_API = 'https://tasks.googleapis.com/tasks/v1';

interface GoogleTaskListsResponse {
  items?: { id: string; title?: string }[];
}
interface GoogleTaskApiItem {
  id: string;
  title?: string;
  status?: string; // 'needsAction' | 'completed'
  due?: string; // RFC3339（日付のみ・時刻は 00:00:00Z）
  notes?: string;
  updated?: string; // RFC3339（最終更新時刻・同期方向の判定に使う）
  deleted?: boolean;
  hidden?: boolean;
}
interface GoogleTasksResponse {
  items?: GoogleTaskApiItem[];
  nextPageToken?: string;
}

/** アプリ表示用に正規化した Google ToDo 1件。 */
export interface GoogleTaskItem {
  /** Google タスク id。 */
  id: string;
  /** 所属タスクリスト id（PATCH に必要）。 */
  tasklistId: string;
  /** 所属タスクリスト名（表示用）。 */
  listTitle: string;
  title: string;
  /** 'YYYY-MM-DD'（期限なしは undefined）。 */
  due?: string;
  completed: boolean;
}

/**
 * ログインユーザーの全タスクリストを横断して ToDo を取得。
 * 失敗・未連携は [] を返す（落とさない）。完了済みも含めて取得（表示側で仕分け）。
 */
export async function fetchGoogleTasks(
  accessToken: string | null | undefined,
): Promise<GoogleTaskItem[]> {
  if (!accessToken) return [];
  const headers = { Authorization: 'Bearer ' + accessToken };
  const out: GoogleTaskItem[] = [];
  try {
    const listRes = await fetch(TASKS_API + '/users/@me/lists?maxResults=100', { headers });
    if (!listRes.ok) {
      console.warn('[google] Tasklists 取得失敗 status=' + listRes.status);
      return [];
    }
    const lists = ((await listRes.json()) as GoogleTaskListsResponse).items || [];
    for (const list of lists) {
      const listTitle = list.title || '(リスト)';
      let pageToken: string | undefined;
      // リストごとに最大4ページ（250×4）取得。
      for (let page = 0; page < 4; page++) {
        const params = new URLSearchParams({ maxResults: '100', showCompleted: 'true', showHidden: 'false' });
        if (pageToken) params.set('pageToken', pageToken);
        const res = await fetch(TASKS_API + '/lists/' + list.id + '/tasks?' + params.toString(), { headers });
        if (!res.ok) break;
        const data = (await res.json()) as GoogleTasksResponse;
        for (const t of data.items || []) {
          if (t.deleted || !t.title) continue; // 削除済み・タイトル空は除外
          out.push({
            id: t.id,
            tasklistId: list.id,
            listTitle,
            title: t.title,
            due: t.due ? t.due.slice(0, 10) : undefined,
            completed: t.status === 'completed',
          });
        }
        pageToken = data.nextPageToken;
        if (!pageToken) break;
      }
    }
    return out;
  } catch (err) {
    console.warn('[google] Tasks API 例外', err);
    return out;
  }
}

/**
 * 双方向同期で扱う Google ToDo 1件（同期判定に必要な full フィールド）。
 * fetchGoogleTasks（表示用 GoogleTaskItem）と別物：notes / status 生値 / updated を保持する。
 */
export interface GoogleSyncTask {
  /** Google タスク id。 */
  id: string;
  /** 所属タスクリスト id（PATCH/同期に必要）。 */
  tasklistId: string;
  title: string;
  /** メモ（双方向対象だが本アプリは未使用＝保持のみ）。 */
  notes?: string;
  /** 'YYYY-MM-DD'（期限なしは undefined）。 */
  due?: string;
  /** Google の生ステータス。'completed' を完了として扱う。 */
  completed: boolean;
  /** Google の最終更新時刻（RFC3339）。pull/push の方向判定の基準。 */
  updated: string;
}

/**
 * 指定タイトルのタスクリストを探し、無ければ作成して id を返す（"TEMP社内ポータル" 用）。
 * GET tasklists で title 完全一致を探索 → 見つからなければ POST tasklists で新規作成。
 * 未連携・失敗・例外は null（落とさない）。
 */
export async function findOrCreateTaskList(
  accessToken: string | null | undefined,
  title: string,
): Promise<string | null> {
  if (!accessToken || !title) return null;
  const headers = { Authorization: 'Bearer ' + accessToken };
  try {
    const listRes = await fetch(TASKS_API + '/users/@me/lists?maxResults=100', { headers });
    if (!listRes.ok) {
      console.warn('[google] Tasklists 取得失敗 status=' + listRes.status);
      return null;
    }
    const lists = ((await listRes.json()) as GoogleTaskListsResponse).items || [];
    const found = lists.find((l) => (l.title || '') === title);
    if (found) return found.id;
    // 無ければ作成。
    const createRes = await fetch(TASKS_API + '/users/@me/lists', {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!createRes.ok) {
      console.warn('[google] Tasklist 作成失敗 status=' + createRes.status);
      return null;
    }
    const created = (await createRes.json()) as { id?: string };
    return created.id || null;
  } catch (err) {
    console.warn('[google] findOrCreateTaskList 例外', err);
    return null;
  }
}

/**
 * 特定タスクリストの ToDo を完了済み・非表示も含めて取得（双方向同期の読み側）。
 * showCompleted=true & showHidden=true で完了タスクも漏らさず拾う（完了同期に必要）。
 * 返り値は同期判定用の GoogleSyncTask[]（updated / notes / status 生値を保持）。
 * 未連携・失敗・例外は []（落とさない）。
 */
export async function fetchGoogleTasksInList(
  accessToken: string | null | undefined,
  tasklistId: string,
): Promise<GoogleSyncTask[]> {
  if (!accessToken || !tasklistId) return [];
  const headers = { Authorization: 'Bearer ' + accessToken };
  const out: GoogleSyncTask[] = [];
  try {
    let pageToken: string | undefined;
    for (let page = 0; page < 10; page++) {
      const params = new URLSearchParams({
        maxResults: '100',
        showCompleted: 'true',
        showHidden: 'true',
      });
      if (pageToken) params.set('pageToken', pageToken);
      const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks?' + params.toString(), {
        headers,
      });
      if (!res.ok) {
        console.warn('[google] Tasks(list) 取得失敗 status=' + res.status);
        break;
      }
      const data = (await res.json()) as GoogleTasksResponse;
      for (const t of data.items || []) {
        if (t.deleted || !t.title) continue; // 削除済み・タイトル空は除外
        out.push({
          id: t.id,
          tasklistId,
          title: t.title,
          notes: t.notes,
          due: t.due ? t.due.slice(0, 10) : undefined,
          completed: t.status === 'completed',
          updated: t.updated || '',
        });
      }
      pageToken = data.nextPageToken;
      if (!pageToken) break;
    }
    return out;
  } catch (err) {
    console.warn('[google] fetchGoogleTasksInList 例外', err);
    return out;
  }
}

/**
 * 既定のタスクリスト（「マイタスク」）の **実 id** を解決する。慣例で先頭のリストが既定。
 * 取得できないときは null を返す＝呼び出し側は同期を丸ごとスキップする。
 *
 * 🔴 2026-07-26 変更（重複タスク事故の対策②）: 以前は失敗時に仮想 id `'@default'` へ
 *    フォールバックしていた。しかし同期メタ（tasks.googleTaskListId）には解決結果が
 *    そのまま保存されるため、実 id を保存した状態で一度でも `'@default'` に落ちると
 *    「保存済み listId ≠ 今回の listId」となり **移行分岐（旧実装は delete→create）が
 *    全タスクで暴発**し、削除に失敗した分だけ Google 側に残骸（重複）が積んだ。
 *    リスト id は同期の同一性の土台なので、**曖昧な値で続行するより同期しない方が安全**。
 */
export async function getDefaultTasklistId(
  accessToken: string | null | undefined,
): Promise<string | null> {
  if (!accessToken) return null;
  try {
    const res = await fetch(TASKS_API + '/users/@me/lists?maxResults=100', {
      headers: { Authorization: 'Bearer ' + accessToken },
    });
    if (!res.ok) {
      // 失敗時は null＝今回は同期しない（次のトリガーで再試行）。
      console.warn('[google] Tasklists 取得失敗 status=' + res.status);
      return null;
    }
    const lists = ((await res.json()) as GoogleTaskListsResponse).items || [];
    // 慣例で先頭が「マイタスク」既定リスト。取れなければ同期しない。
    return lists[0]?.id || null;
  } catch (err) {
    console.warn('[google] Tasklists 取得例外', err);
    return null;
  }
}

/** create/update のリクエストボディ（title/due の編集）。 */
export interface GoogleTaskWrite {
  /** タスク名。create では必須・update では省略可。 */
  title?: string;
  /** 'YYYY-MM-DD'。空文字 / null で期日なし。 */
  due?: string | null;
}

/** Google API の due（RFC3339 日付・時刻は 00:00:00Z）と 'YYYY-MM-DD' の相互変換。 */
function ymdToRfc3339(dueYmd: string | null | undefined): string | null {
  if (!dueYmd) return null;
  return new Date(dueYmd + 'T00:00:00Z').toISOString();
}

/**
 * Google ToDo を新規作成する（双方向同期の書き側・作成）。
 * POST /lists/{tasklistId}/tasks。作成された行を内部 GoogleTaskItem に正規化して返す。
 *
 * @param tasklistId 作成先リスト id（getDefaultTasklistId で解決した値 or 既存タスクの tasklistId）。
 * @param input      title（必須）＋ due（任意・'YYYY-MM-DD'）。
 * @returns 作成された GoogleTaskItem。未連携・title 空・失敗・例外は null（落とさない）。
 */
export async function createGoogleTask(
  accessToken: string | null | undefined,
  tasklistId: string,
  input: GoogleTaskWrite,
): Promise<GoogleTaskItem | null> {
  if (!accessToken || !tasklistId) return null;
  const title = (input.title || '').trim();
  if (!title) return null;
  try {
    const body: Record<string, unknown> = { title };
    const due = ymdToRfc3339(input.due);
    if (due) body.due = due;
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.warn('[google] Task 作成失敗 status=' + res.status);
      return null;
    }
    const t = (await res.json()) as GoogleTaskApiItem;
    return {
      id: t.id,
      tasklistId,
      // 作成APIはリスト名を返さないため、表示用に既定名を当てる（一覧再取得で正しい名前に揃う）。
      listTitle: 'マイタスク',
      title: t.title || title,
      due: t.due ? t.due.slice(0, 10) : undefined,
      completed: t.status === 'completed',
    };
  } catch (err) {
    console.warn('[google] Task 作成例外', err);
    return null;
  }
}

/**
 * 双方向同期用に Google ToDo を新規作成する（アプリタスク → Google・初回ミラー）。
 * createGoogleTask と違い、完了状態の同時指定と Google の `updated` 取得に対応した
 * GoogleSyncTask を返す（マッピング保存に `updated` が要る）。
 *
 * @param tasklistId 作成先リスト id（デフォルト「マイタスク」リストの id）。
 * @param input title（必須）/ due（任意・'YYYY-MM-DD'）/ completed（任意・既定 false）。
 * @returns 作成された GoogleSyncTask。未連携・title 空・失敗・例外は null（落とさない）。
 */
export async function createGoogleSyncTask(
  accessToken: string | null | undefined,
  tasklistId: string,
  input: { title: string; due?: string | null; completed?: boolean },
): Promise<GoogleSyncTask | null> {
  if (!accessToken || !tasklistId) return null;
  const title = (input.title || '').trim();
  if (!title) return null;
  try {
    const body: Record<string, unknown> = { title };
    const due = ymdToRfc3339(input.due);
    if (due) body.due = due;
    if (input.completed) body.status = 'completed';
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.warn('[google] SyncTask 作成失敗 status=' + res.status);
      return null;
    }
    const t = (await res.json()) as GoogleTaskApiItem;
    return {
      id: t.id,
      tasklistId,
      title: t.title || title,
      notes: t.notes,
      due: t.due ? t.due.slice(0, 10) : undefined,
      completed: t.status === 'completed',
      updated: t.updated || '',
    };
  } catch (err) {
    console.warn('[google] SyncTask 作成例外', err);
    return null;
  }
}

/**
 * 1件の Google ToDo の現在状態を取得する（同期の書き込み後に新しい `updated` を読むため）。
 * GET /lists/{tasklistId}/tasks/{taskId}。未連携・失敗・例外は null（落とさない）。
 */
export async function getGoogleSyncTask(
  accessToken: string | null | undefined,
  tasklistId: string,
  taskId: string,
): Promise<GoogleSyncTask | null> {
  if (!accessToken || !tasklistId || !taskId) return null;
  try {
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks/' + taskId, {
      headers: { Authorization: 'Bearer ' + accessToken },
    });
    if (!res.ok) {
      console.warn('[google] SyncTask 取得失敗 status=' + res.status);
      return null;
    }
    const t = (await res.json()) as GoogleTaskApiItem;
    if (!t.id) return null;
    return {
      id: t.id,
      tasklistId,
      title: t.title || '',
      notes: t.notes,
      due: t.due ? t.due.slice(0, 10) : undefined,
      completed: t.status === 'completed',
      updated: t.updated || '',
    };
  } catch (err) {
    console.warn('[google] SyncTask 取得例外', err);
    return null;
  }
}

/**
 * Google ToDo の title / due を編集する（双方向同期の書き側・更新）。
 * PATCH /lists/{tasklistId}/tasks/{taskId}。指定したフィールドだけを部分更新する。
 *
 * @param patch title（任意）・due（任意・'YYYY-MM-DD'・null で期日削除）。両方省略は no-op で true。
 * @returns 成功で true。未連携・失敗・例外は false（落とさない）。
 *
 * 注: 期日のみの更新は setGoogleTaskDue でも可。本関数は title 編集（＋due 同時編集）用。
 */
export async function updateGoogleTask(
  accessToken: string | null | undefined,
  tasklistId: string,
  taskId: string,
  patch: GoogleTaskWrite,
): Promise<boolean> {
  if (!accessToken || !tasklistId || !taskId) return false;
  // 送るフィールドだけを body に積む（未指定は触らない＝PATCH の部分更新）。
  const body: Record<string, unknown> = {};
  if (typeof patch.title === 'string') {
    const title = patch.title.trim();
    if (!title) return false; // タイトル空への更新は弾く（Google も拒否する）
    body.title = title;
  }
  if (patch.due !== undefined) body.due = ymdToRfc3339(patch.due); // null=期日削除
  if (Object.keys(body).length === 0) return true; // 変更なし
  try {
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks/' + taskId, {
      method: 'PATCH',
      headers: { Authorization: 'Bearer ' + accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.warn('[google] Task 更新失敗 status=' + res.status);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[google] Task 更新例外', err);
    return false;
  }
}

/**
 * Google ToDo の完了/未完了を書き戻す（双方向同期の書き側）。
 * 成功で true。完了時は status='completed'、戻すときは 'needsAction'（completed をクリア）。
 */
export async function setGoogleTaskCompleted(
  accessToken: string | null | undefined,
  tasklistId: string,
  taskId: string,
  completed: boolean,
): Promise<boolean> {
  if (!accessToken) return false;
  try {
    const body = completed
      ? { status: 'completed' }
      : { status: 'needsAction', completed: null };
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks/' + taskId, {
      method: 'PATCH',
      headers: { Authorization: 'Bearer ' + accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.warn('[google] Task PATCH 失敗 status=' + res.status);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[google] Task PATCH 例外', err);
    return false;
  }
}

/**
 * Google ToDo の期日（due）を書き戻す（双方向同期の書き側）。
 * Google の due は RFC3339 の「日付」（時刻は 00:00:00Z）として保持される。
 *
 * @param dueYmd 'YYYY-MM-DD'。null を渡すと期日を削除する。
 * @returns 成功で true。未連携・失敗・例外は false（落とさない）。
 */
export async function setGoogleTaskDue(
  accessToken: string | null | undefined,
  tasklistId: string,
  taskId: string,
  dueYmd: string | null,
): Promise<boolean> {
  if (!accessToken) return false;
  try {
    // 'YYYY-MM-DD' → RFC3339（例 '2026-06-30T00:00:00.000Z'）。null は期日削除。
    const due = dueYmd ? new Date(dueYmd + 'T00:00:00Z').toISOString() : null;
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks/' + taskId, {
      method: 'PATCH',
      headers: { Authorization: 'Bearer ' + accessToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({ due }),
    });
    if (!res.ok) {
      console.warn('[google] Task due PATCH 失敗 status=' + res.status);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[google] Task due PATCH 例外', err);
    return false;
  }
}

/**
 * Google ToDo を削除する（双方向同期の移行・リスト切替用）。
 * DELETE /lists/{tasklistId}/tasks/{taskId}。旧「TEMP社内ポータル」リストに作った
 * タスクをデフォルト「マイタスク」リストに作り直す際の旧側削除に使う（best-effort）。
 * 未連携・失敗・例外は false（落とさない）。404（既に無い）も false 扱いで呼び出し側は続行する。
 */
export async function deleteGoogleTask(
  accessToken: string | null | undefined,
  tasklistId: string,
  taskId: string,
): Promise<boolean> {
  if (!accessToken || !tasklistId || !taskId) return false;
  try {
    const res = await fetch(TASKS_API + '/lists/' + tasklistId + '/tasks/' + taskId, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + accessToken },
    });
    // 404/410 ＝ Google 側に既に存在しない＝**望む終状態**なので成功として扱う
    //（2026-07-26。ここで false を返すと呼び出し側が「消せなかった」と誤認する）。
    if (res.status === 404 || res.status === 410) return true;
    if (!res.ok) {
      console.warn('[google] Task 削除失敗 status=' + res.status);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[google] Task 削除例外', err);
    return false;
  }
}

// ============================================================
// GOOGLE DRIVE API (C4) — ログインユーザーの実 Drive ファイル読み取り。
//
// scope 'https://www.googleapis.com/auth/drive.readonly'（2026-07-10 に要求スコープから除去済み）の Bearer で
// Drive API v3 files.list を直叩き。webViewLink で実「Driveで開く」リンクを得る。
// v1 は「My Drive＋共有アイテムの最近のファイル」（必要なら query で絞り込み）。
// 共有ドライブ(TEMP)横断や PJフォルダ紐付けは v2（corpora=allDrives / フォルダID指定）。
// ============================================================
const DRIVE_FILES_API = 'https://www.googleapis.com/drive/v3/files';

interface GoogleDriveApiFile {
  id: string;
  name?: string;
  mimeType?: string;
  webViewLink?: string;
  iconLink?: string;
  modifiedTime?: string;
}
interface GoogleDriveListResponse {
  files?: GoogleDriveApiFile[];
}

/** アプリ表示用に正規化した Drive ファイル1件。 */
export interface DriveFileItem {
  id: string;
  name: string;
  mimeType: string;
  /** 実「Driveで開く」リンク（権限内のみ開ける）。 */
  webViewLink?: string;
  /** ファイル種別アイコン URL。 */
  iconLink?: string;
  /** 'YYYY-MM-DD'（更新日）。 */
  modified?: string;
}

/**
 * ログインユーザーの Drive ファイルを取得（既定は最近更新順）。
 * opts.query を渡すと Drive 検索クエリで絞り込む（例: "name contains 'xFactory'"）。
 * 失敗・未連携は []（落とさない）。
 * ⚠️ 現在デッドコード：drive.readonly スコープ除去（2026-07-10）で呼び出し元（Projects.tsx の
 *    LiveDriveCard）は SHOW_DRIVE_FILES=false で無効化済み。復活時は権限再追加＋CASA審査が必要。
 */
export async function fetchDriveFiles(
  accessToken: string | null | undefined,
  opts?: { query?: string; pageSize?: number },
): Promise<DriveFileItem[]> {
  if (!accessToken) return [];
  // trashed=false を常に AND（query 指定時も）。
  const q = opts?.query ? '(' + opts.query + ') and trashed=false' : 'trashed=false';
  const params = new URLSearchParams({
    pageSize: String(opts?.pageSize ?? 20),
    fields: 'files(id,name,mimeType,webViewLink,iconLink,modifiedTime)',
    orderBy: 'modifiedTime desc',
    q,
    includeItemsFromAllDrives: 'true',
    supportsAllDrives: 'true',
  });
  try {
    const res = await fetch(DRIVE_FILES_API + '?' + params.toString(), {
      headers: { Authorization: 'Bearer ' + accessToken },
    });
    if (!res.ok) {
      console.warn('[google] Drive files 取得失敗 status=' + res.status);
      return [];
    }
    const data = (await res.json()) as GoogleDriveListResponse;
    return (data.files || []).map((f) => ({
      id: f.id,
      name: f.name || '(無題)',
      mimeType: f.mimeType || '',
      webViewLink: f.webViewLink,
      iconLink: f.iconLink,
      modified: f.modifiedTime ? f.modifiedTime.slice(0, 10) : undefined,
    }));
  } catch (err) {
    console.warn('[google] Drive API 例外', err);
    return [];
  }
}
